/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

/**
 * Gets the base URL from Vite's environment.
 * BASE_URL is typically '/' in development and can be configured for production deployments.
 */

import { AccessCodeV1, AccessCodeVersion } from '@karya/core';

export const getBaseUrl = (): string => {
  return import.meta.env.BASE_URL || '/';
};

/**
 * Constructs a full URL path using the base URL.
 * The path is always treated as relative to the base URL.
 *
 * @param path - The path to append to the base URL (e.g., '/logo.svg').
 * @returns The full path with base URL (e.g., '/subdir/logo.svg' or '/logo.svg').
 */
export const withBaseUrl = (path: string): string => {
  const baseUrl = getBaseUrl();

  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return `${normalizedBase}${normalizedPath}`;
};

/**
 * Resolves a full box URL based on the access code version.
 *
 * Requires `VITE_V0_ACCESS_CODE_BOX_URL` and `VITE_V1_ACCESS_CODE_CONFIG` to be set in the environment.
 *
 * @param accessCode - The access code string (must be a valid integer).
 * @param url - The relative URL path to resolve against the box base URL.
 * @param queryParams - An object representing query parameters to append to the URL.
 * @returns The fully resolved URL string.
 * @throws {Error} If the access code is not an integer or if the version is unsupported.
 */
export function resolveBoxUrl(
  accessCode: string,
  url: string,
  queryParams?: Record<string, string | undefined>,
): string {
  const accessCodeInt = Number.parseInt(accessCode);
  const defaultBoxUrl = import.meta.env.VITE_V0_ACCESS_CODE_BOX_URL;
  if (isNaN(accessCodeInt)) throw new Error('Access code is not an integer');

  // Extract version and length
  const accessCodeBinaryString = accessCodeInt.toString(2);
  const accessCodeVersion = Number.parseInt(accessCodeBinaryString.slice(-2), 2);
  let baseBoxUrl: string;
  switch (accessCodeVersion) {
    case AccessCodeVersion.V0:
      if (!defaultBoxUrl) {
        throw new Error('VITE_V0_ACCESS_CODE_BOX_URL environment variable is not configured');
      }
      baseBoxUrl = defaultBoxUrl;
      break;

    case AccessCodeVersion.V1: {
      try {
        const v1Config = JSON.parse(import.meta.env.VITE_V1_ACCESS_CODE_CONFIG);
        const parsedAccessCode = AccessCodeV1.parseV1Code(accessCode, v1Config);
        if (!parsedAccessCode.box_url) {
          throw new Error('Box URL not found in access code');
        }
        baseBoxUrl = parsedAccessCode.box_url;
      } catch (error) {
        throw new Error('Failed to parse V1 access code configuration', { cause: error });
      }
      break;
    }
    default:
      throw new Error('Unsupported access code version');
  }

  if (queryParams && Object.keys(queryParams).length > 0) {
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([_, value]) => value !== undefined),
    ) as Record<string, string>;

    const queryString = new URLSearchParams(filteredParams).toString();
    url += `?${queryString}`;
  }

  return new URL(url, baseBoxUrl).toString();
}
